import React, { useState, useRef, useCallback } from 'react';
import {
    Container, Box, Typography, Button, Paper, LinearProgress, Alert, Snackbar,
    Divider, Chip, Stack, CircularProgress
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Download as DownloadIcon,
    Description as DescriptionIcon,
    CheckCircle as CheckCircleIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon
} from '@mui/icons-material';

interface ConversionResult {
    conversionId: string;
    convertedFilename: string;
    downloadUrl: string;
    structuredData: any;
    originalFilename: string;
    fileSize: number;
}

interface ProgressUpdate {
    stage: string;
    progress: number;
    message: string;
    timestamp: string;
}

const CVConverterPage: React.FC = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isConverting, setIsConverting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [conversionResult, setConversionResult] = useState<ConversionResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);

    const isValidFile = (file: File) => {
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX only
        ];
        return validTypes.includes(file.type) && file.size <= 10 * 1024 * 1024; // 10MB limit
    };

    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setIsDragActive(true);
        } else if (e.type === 'dragleave') {
            setIsDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (isValidFile(file)) {
                setSelectedFile(file);
                setError(null);
            } else {
                setError('Please select a valid Word document (.docx file, max 10MB)');
            }
        }
    }, []);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (isValidFile(file)) {
                setSelectedFile(file);
                setError(null);
            } else {
                setError('Please select a valid Word document (.docx file, max 10MB)');
                e.target.value = '';
            }
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const convertCV = async () => {
        if (!selectedFile) return;

        setIsConverting(true);
        setProgress(0);
        setProgressMessage('Starting conversion...');
        setError(null);
        setConversionResult(null);

        try {
            const formData = new FormData();
            formData.append('resume', selectedFile);

            setProgress(20);
            setProgressMessage('Uploading file...');

            const response = await fetch('/api/CVConverterUploadFunction', {
                method: 'POST',
                body: formData,
            });

            setProgress(50);
            setProgressMessage('Processing upload...');

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const errorMessage = errorData?.details || errorData?.error || `Upload failed: ${response.status}`;
                throw new Error(errorMessage);
            }

            const responseData = await response.json();
            setProgress(80);
            setProgressMessage('Processing complete...');

            if (responseData.success) {
                const conversionData = {
                    conversionId: responseData.data.conversionId,
                    convertedFilename: responseData.data.convertedFilename,
                    downloadUrl: responseData.data.downloadUrl,
                    structuredData: responseData.data.structuredData,
                    originalFilename: responseData.data.originalFilename,
                    fileSize: responseData.data.fileSize
                };

                setConversionResult(conversionData);
                setProgress(100);
                setProgressMessage('Conversion completed successfully!');
                setSnackbarMessage('CV converted to IOD PARC format successfully!');
                setSnackbarOpen(true);
            } else {
                throw new Error(responseData.error || responseData.details || 'Conversion failed');
            }
        } catch (error) {
            console.error('Conversion error:', error);
            setError(error instanceof Error ? error.message : 'Conversion failed');
            setProgress(0);
            setProgressMessage('');
        } finally {
            setIsConverting(false);
        }
    };

    const downloadConvertedCV = async () => {
        if (!conversionResult) return;

        try {
            const response = await fetch(conversionResult.downloadUrl, {
                method: 'GET'
            });

            if (!response.ok) {
                throw new Error('Download failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = conversionResult.convertedFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSnackbarMessage('CV downloaded successfully!');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Download error:', error);
            setError('Failed to download converted CV');
        }
    };

    const resetForm = () => {
        setSelectedFile(null);
        setConversionResult(null);
        setProgress(0);
        setProgressMessage('');
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <Container maxWidth="xl" sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            {/* Header Section */}
            <Box sx={{ flexShrink: 0, mb: 3 }}>
                <Typography variant="h4" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 'bold',
                    mb: 1
                }}>
                    CV Converter
                </Typography>

                <Typography variant="body1" sx={{
                    mb: 3,
                    color: 'text.secondary'
                }}>
                    Transform your CV to IOD PARC format using AI-powered analysis. Upload a Word document (.docx) to get started.
                </Typography>
            </Box>

            {/* Two-Column Layout */}
            <Box sx={{
                display: 'flex',
                gap: 4,
                flexGrow: 1,
                '@media (max-width: 900px)': {
                    flexDirection: 'column'
                }
            }}>
                {/* Left Column - Processing Guidelines (condensed) */}
                <Box sx={{
                    flex: '0 0 360px',
                    '@media (max-width: 900px)': {
                        flex: '1 1 auto'
                    }
                }}>
                    <Box sx={{
                        p: 2.5,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.03) 100%)',
                        border: '1px solid rgba(25,118,210,0.14)',
                        backdropFilter: 'blur(10px)',
                        position: 'relative',
                        overflow: 'hidden',
                        height: 'fit-content',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: 'linear-gradient(90deg, transparent, rgba(25,118,210,0.6), transparent)',
                            opacity: 0.8
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Box sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2
                            }}>
                                <Typography sx={{ fontSize: '18px' }}>🎯</Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>Guidelines</Typography>
                        </Box>

                        <Stack spacing={1.5}>
                            <Box sx={{
                                p: 2.5,
                                borderRadius: '12px',
                                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                border: '1px solid rgba(76, 175, 80, 0.2)',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '3px',
                                    background: 'rgba(76, 175, 80, 0.5)',
                                    borderRadius: '0 12px 12px 0'
                                }
                            }}>
                                <Typography variant="body2" sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 0.5
                                }}>
                                    <Box sx={{ fontSize: '16px' }}>✨</Box>
                                    <strong>Optimal Results</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Best results with CVs up to 10–12 pages.</Typography>
                            </Box>

                            <Box sx={{
                                p: 2.5,
                                borderRadius: '12px',
                                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                border: '1px solid rgba(255, 152, 0, 0.2)',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '3px',
                                    background: 'rgba(255, 152, 0, 0.5)',
                                    borderRadius: '0 12px 12px 0'
                                }
                            }}>
                                <Typography variant="body2" sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 1.5
                                }}>
                                    <Box sx={{ fontSize: '16px' }}>⚡</Box>
                                    <strong>Large CVs (15+ pages)</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>For large CVs (15+ pages):</Typography>
                                <Stack spacing={1}>
                                    <Typography variant="body2" color="text.secondary" sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        lineHeight: 1.6
                                    }}>
                                        <Box sx={{
                                            width: 5,
                                            height: 5,
                                            borderRadius: '50%',
                                            backgroundColor: 'rgba(255, 152, 0, 0.6)',
                                            mt: 0.75,
                                            flexShrink: 0
                                        }} />
                                        Copy/paste into a new Word document to reduce file size
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        lineHeight: 1.6
                                    }}>
                                        <Box sx={{
                                            width: 5,
                                            height: 5,
                                            borderRadius: '50%',
                                            backgroundColor: 'rgba(255, 152, 0, 0.6)',
                                            mt: 0.75,
                                            flexShrink: 0
                                        }} />
                                        Focus on the most recent 10–15 years
                                    </Typography>
                                </Stack>
                            </Box>

                            <Box sx={{
                                p: 2.5,
                                borderRadius: '12px',
                                backgroundColor: 'rgba(156, 39, 176, 0.08)',
                                border: '1px solid rgba(156, 39, 176, 0.2)',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '3px',
                                    background: 'rgba(156, 39, 176, 0.5)',
                                    borderRadius: '0 12px 12px 0'
                                }
                            }}>
                                <Typography variant="body2" sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 0.5
                                }}>
                                    <Box sx={{ fontSize: '16px' }}>💎</Box>
                                    <strong>Pro Tip</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                    The "copy/paste into new document" trick often moves large CVs from basic to comprehensive processing.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Box>

                {/* Right Column - Upload and Results */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '600px'
                }}>
                    {/* File Upload Area */}
                    <Paper
                        sx={{
                            p: 5,
                            mb: 3,
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : 'rgba(255, 255, 255, 0.2)',
                            backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.05)' : 'rgba(255, 255, 255, 0.02)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            borderRadius: '16px',
                            boxShadow: isDragActive
                                ? '0px 8px 32px rgba(25, 118, 210, 0.2)'
                                : '0px 4px 20px rgba(0, 0, 0, 0.1)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'rgba(25, 118, 210, 0.03)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0px 8px 28px rgba(0, 0, 0, 0.15)'
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '2px',
                                background: isDragActive
                                    ? 'linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.8), transparent)'
                                    : 'transparent',
                                opacity: isDragActive ? 1 : 0,
                                transition: 'opacity 0.3s ease'
                            }
                        }}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={handleUploadClick}
                    >
                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3,
                                transition: 'all 0.3s ease',
                                ...(isDragActive && {
                                    backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                    transform: 'scale(1.1)'
                                })
                            }}>
                                <CloudUploadIcon sx={{
                                    fontSize: 40,
                                    color: 'primary.main',
                                    transition: 'all 0.3s ease',
                                    ...(isDragActive && {
                                        transform: 'scale(1.2)'
                                    })
                                }} />
                            </Box>

                            <Typography variant="h6" gutterBottom sx={{
                                fontWeight: 600,
                                color: selectedFile ? 'success.main' : 'text.primary'
                            }}>
                                {selectedFile ? selectedFile.name : 'Drop your CV here or click to browse'}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Supports .docx files up to 10MB
                            </Typography>

                            {selectedFile && (
                                <Chip
                                    label={`${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`}
                                    color="success"
                                    variant="outlined"
                                    sx={{
                                        mt: 1,
                                        fontWeight: 500,
                                        borderWidth: 2
                                    }}
                                    onDelete={() => {
                                        setSelectedFile(null);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                        }
                                    }}
                                    deleteIcon={<CloseIcon />}
                                />
                            )}
                        </Box>
                    </Paper>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".docx"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        {!isConverting && (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={convertCV}
                                disabled={!selectedFile}
                                startIcon={<DescriptionIcon />}
                                sx={{
                                    minWidth: 240,
                                    height: 56,
                                    borderRadius: '12px',
                                    background: !selectedFile
                                        ? 'rgba(255, 255, 255, 0.1)'
                                        : 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                                    boxShadow: !selectedFile
                                        ? 'none'
                                        : '0 4px 20px rgba(25, 118, 210, 0.3)',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        background: !selectedFile
                                            ? 'rgba(255, 255, 255, 0.1)'
                                            : 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                                        boxShadow: !selectedFile
                                            ? 'none'
                                            : '0 6px 24px rgba(25, 118, 210, 0.4)',
                                        transform: !selectedFile ? 'none' : 'translateY(-2px)'
                                    },
                                    '&:disabled': {
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        color: 'rgba(255, 255, 255, 0.3)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Convert to IOD PARC Format
                            </Button>
                        )}

                        {/* Only show Convert Another CV button after successful conversion */}
                        {conversionResult && (
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={resetForm}
                                startIcon={<RefreshIcon />}
                                disabled={isConverting}
                                sx={{
                                    minWidth: 200,
                                    height: 56,
                                    borderRadius: '12px',
                                    borderWidth: 2,
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                    color: 'text.primary',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderWidth: 2,
                                        borderColor: 'primary.main',
                                        backgroundColor: 'rgba(25, 118, 210, 0.05)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 16px rgba(25, 118, 210, 0.2)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Convert Another CV
                            </Button>
                        )}
                    </Box>

                    {/* Progress Display */}
                    {isConverting && (
                        <Box sx={{
                            width: '100%',
                            mb: 3,
                            p: 3,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(25, 118, 210, 0.05)',
                            border: '1px solid rgba(25, 118, 210, 0.2)'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CircularProgress size={20} sx={{ mr: 1.5, color: 'primary.main' }} />
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    Processing Your CV
                                </Typography>
                            </Box>

                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '& .MuiLinearProgress-bar': {
                                        background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                                        borderRadius: 4
                                    }
                                }}
                            />

                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1.5 }}>
                                {progress < 100 ? `${progressMessage} ${Math.round(progress)}%` : 'Finalizing...'}
                            </Typography>
                        </Box>
                    )}

                    {/* Error Display */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: '12px',
                                backgroundColor: 'rgba(244, 67, 54, 0.05)',
                                border: '1px solid rgba(244, 67, 54, 0.2)',
                                '& .MuiAlert-icon': {
                                    fontSize: 24
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}
                </Box>

                {/* Results Area - matching SearchPageComponent results structure */}
                <Box sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    mt: 2
                }}>
                    {/* Conversion Result - concise, modern summary */}
                    {conversionResult && (
                        <Paper sx={{
                            mb: 3,
                            p: 3,
                            borderRadius: '12px',
                            boxShadow: '0px 6px 18px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(76,175,80,0.25)',
                            background: 'rgba(76,175,80,0.04)'
                        }}>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 26 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Converted</Typography>
                            </Stack>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {conversionResult.convertedFilename}
                            </Typography>

                            <Chip
                                size="small"
                                label={`Size ${(conversionResult.fileSize / 1024 / 1024).toFixed(2)} MB`}
                                sx={{
                                    width: 'fit-content',
                                    borderRadius: '8px',
                                    borderColor: 'rgba(76,175,80,0.4)'
                                }}
                                variant="outlined"
                            />

                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        minWidth: 240,
                                        height: 48,
                                        borderRadius: '8px',
                                        background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                                        boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
                                        '&:hover': {
                                            background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                                            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                                            transform: 'translateY(-1px)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                    startIcon={<DownloadIcon />}
                                    onClick={downloadConvertedCV}
                                >
                                    Download CV
                                </Button>
                            </Box>
                        </Paper>
                    )}

                    {/* No results state - matching SearchPageComponent pattern */}
                    {!isConverting && !conversionResult && !error && (
                        <Box sx={{
                            textAlign: 'center',
                            mt: 6,
                            p: 4,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                            <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                Ready to Convert
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Upload a Word document (.docx) to begin CV transformation to IOD PARC format
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Snackbar for notifications - matching main app pattern */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </Container>
    );
};

export default CVConverterPage; 